<template>
  <div id="app">
    <div class="jukebox-container">
      <JukeboxWidget :songs="songs" :loading="loading" />
    </div>
  </div>
</template>

<script>
import JukeboxWidget from './components/JukeboxWidget.vue'
import { ref, onMounted } from 'vue'

export default {
  name: 'App',
  components: {
    JukeboxWidget,
  },
  setup() {
    const songs = ref([])
    const loading = ref(true)

    // Field mapping for easy updates if column names change
    const fieldMapping = {
      date: 'Datum',
      artistTitle: 'Artiest - Nummer',
      listener: 'Luisteraar',
    }

    // Parse artist and title from artist_title field
    const parseArtistTitle = (artistTitle) => {
      if (!artistTitle || artistTitle.trim() === '') {
        return { artist: '', title: '' }
      }

      // Remove any notes in parentheses or special instructions
      const cleaned = artistTitle
        .replace(/\s*\(.*?\)/g, '')
        .replace(/\s*-\s*QUOTE.*$/i, '')
        .trim()

      // Split on ' - ' to separate artist and title
      const parts = cleaned.split(' - ')
      if (parts.length >= 2) {
        return {
          artist: parts[0].trim(),
          title: parts.slice(1).join(' - ').trim(),
        }
      } else {
        // If no ' - ' separator, treat whole string as title
        return {
          artist: '',
          title: cleaned,
        }
      }
    }

    // Format listener name as "FirstName L"
    const formatListenerName = (listenerName) => {
      if (!listenerName || listenerName.trim() === '') {
        return ''
      }

      // Clean up any notes in parentheses or special instructions
      const cleaned = listenerName
        .replace(/\s*\(.*?\)/g, '')
        .replace(/\s*\n.*$/g, '')
        .trim()

      // Split name parts
      const nameParts = cleaned.split(/\s+/).filter((part) => part.length > 0)

      if (nameParts.length === 0) {
        return ''
      } else if (nameParts.length === 1) {
        // Only first name
        return nameParts[0]
      } else {
        // First name + first initial of last name
        const firstName = nameParts[0]
        const lastNameInitial = nameParts[nameParts.length - 1][0].toUpperCase()
        return `${firstName} ${lastNameInitial}`
      }
    }

    // Generate selection codes (like A1, A2, B1, etc.)
    const generateSelectionCode = (index) => {
      const letters = ['A', 'B', 'C', 'D', 'E', 'F']
      const letterIndex = Math.floor(index / 9)
      const numberIndex = (index % 9) + 1

      const letter = letters[letterIndex] || 'A'
      const code = `${letter}${numberIndex}`

      return {
        code1: code,
        code2: `${letter}7${letter}${numberIndex}`, // Generate a secondary code format
      }
    }

    // Fetch songs from Google Sheets API
    const fetchSongs = async () => {
      loading.value = true
      try {
        const response = await fetch(
          'https://spreadsheet.radio.dpgmedia.cloud/prod/fetch?provider=google&spreadsheetId=1HRrfgELADmO6bcxqJUMMuZrltqcx5BbxIy0lqMPswcw'
        )
        const data = await response.json()

      // Filter out empty entries and parse the data
      const validEntries = data.filter(
        (entry) =>
          entry[fieldMapping.artistTitle] &&
          entry[fieldMapping.artistTitle].trim() !== '' &&
          entry[fieldMapping.date] &&
          entry[fieldMapping.date].trim() !== ''
      )

      // Transform the data to match our jukebox format
      songs.value = validEntries.map((entry, index) => {
        const { artist, title } = parseArtistTitle(entry[fieldMapping.artistTitle])
        const codes = generateSelectionCode(index)
        const formattedListener = formatListenerName(entry[fieldMapping.listener])

        return {
          title,
          artist,
          code1: codes.code1,
          code2: codes.code2,
          date: entry[fieldMapping.date],
          listener: formattedListener,
        }
      })
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      fetchSongs()
    })

    return {
      songs,
      loading,
    }
  },
}
</script>

<style scoped>
@import '@dpgradio/creative/styles/fonts/joe';

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body {
  font-family: 'Geomanist', 'Arial', sans-serif;
  background-color: #f5f5f5;
}

#app {
  padding: 20px;
}

.jukebox-container {
  width: 100%;
}
</style>
